import { Badge } from '@abyss/web/ui/Badge';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import find from 'lodash/find';
import { useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { VirtualCareProviderModal } from '../../frontends/ProviderSearch/routes/MapView/VirtualCareProvidersBanner/VirtualCareProvidersModal';
import { useLagoon } from '../../hooks/useLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { getProviderTitle } from '../Utils';
import {
  NetworkWrapper,
  VirtualVisitLabel,
  getBadgeStyles,
  getCardStyles,
  getImageStyles,
  styles,
} from './EnhancedVirtualVisitCard.style';

export const EnhancedVirtualVisitCard = ({
  provider,
  serviceType,
  virtualCareProviders,
}) => {
  const phoneScreen = useMediaQuery(phoneOnly);
  const lagoonData = useLagoon('ui-messaging')();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const vcCostEstimateMessage = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.VIRTUAL_CARE_COST_ESTIMATE_MESSAGE,
  });
  const YouPaySection = () => (
    <Flex
      alignItems={phoneScreen ? 'flex-start' : 'center'}
      css={{
        gap: '8px',
        color: '#323333',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        fontSize: phoneScreen ? '0px' : '14px',
        lineHeight: phoneScreen ? '0px' : '20px',
      }}
      direction="column"
      justify="center"
    >
      <Text
        css={{
          fontFamily: 'UHC2020 Sans',
          fontWeight: '600',
        }}
      >
        {t('VIRTUAL_CARE_PROVIDERS_BANNER.YOU_PAY')}
      </Text>
      <Flex
        alignItems="center"
        css={{ gap: phoneScreen ? '11px' : '8px' }}
        direction="row"
        justify="center"
      >
        <Text
          css={{
            fontWeight: '500',
          }}
        >
          {t('VIRTUAL_CARE_PROVIDERS_BANNER.VARIES')}
        </Text>
        <Popover
          content={vcCostEstimateMessage?.message}
          onOpenChange={() => setIsPopoverOpen(!isPopoverOpen)}
          open={isPopoverOpen}
          title={`${t('Cost estimate')}`}
        >
          <IconSymbol
            color="#196ECF"
            data-auto-testid="virtual-care-list-cost-estimate-popover-icon"
            data-testid="virtual-care-list-cost-estimate-popover-icon"
            icon="info"
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            size="18"
            variant="outlined"
          />
        </Popover>
      </Flex>
    </Flex>
  );
  return (
    <>
      <Card
        css={getCardStyles(phoneScreen) as any}
        data-testid="enhanced-virtual-care-card"
      >
        <Card.Section css={{ margin: '0px' }}>
          <Flex
            css={{
              gap: phoneScreen ? '10px' : '26px',
              flexWrap: 'nowrap',
            }}
            direction="row"
          >
            <div style={styles.imageContainer}>
              {provider?.logo && provider?.logo !== '' ? (
                <Flex
                  alignItems="center"
                  css={{
                    width: '100%',
                    gap: phoneScreen ? '24px' : '18px',
                  }}
                  data-auto-testid="Logo-available"
                  data-testid="Logo-available"
                  direction="column"
                >
                  <img
                    alt={`${provider?.providerName} logo`}
                    src={provider?.logo}
                    style={getImageStyles(phoneScreen)}
                  />
                  <YouPaySection />
                </Flex>
              ) : (
                <Flex
                  alignItems="center"
                  css={{ width: '100%', gap: phoneScreen ? '25px' : '25px' }}
                  data-auto-testid="Logo-not-available"
                  data-testid="Logo-not-available"
                  direction="column"
                >
                  <Badge
                    css={getBadgeStyles(phoneScreen) as any}
                    variant="neutral"
                  >
                    {t('Logo not available')}
                  </Badge>
                  <YouPaySection />
                </Flex>
              )}
            </div>
            <Flex
              alignItems="flex-start"
              css={{ flex: 1, width: '100%' }}
              direction="column"
            >
              <Link
                aria-label={`${provider.providerName} link`}
                css={{
                  fontSize: '18px',
                  lineHeight: '24px',
                  letterSpacing: '0%',
                  color: '$info1',
                  fontWeight: '$bold',
                }}
                data-auto-testid="virtual-care-provider-link"
                data-testid="virtual-care-provider-link"
                isStandardAnchor
                onClick={(_e) => {
                  setIsModalOpen(true);
                }}
                variant="custom"
              >
                {provider?.providerName}
              </Link>
              <Text
                css={{
                  color: '#323333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '16px',
                }}
              >
                {getProviderTitle(serviceType, lagoonData, provider)}
              </Text>
              <NetworkWrapper data-testid="enhanced-virtual-care-card-in-network">
                <IconMaterial color="$success1" icon="check_circle" />
                <Text css={{ fontSize: phoneScreen ? '13px' : '16px' }}>
                  {t('In-network')}
                </Text>
              </NetworkWrapper>
              <VirtualVisitLabel data-testid="enhanced-virtual-care-card-virtual-care-only">
                <IconMaterial icon="videocam" />
                <Text css={{ fontSize: phoneScreen ? '13px' : '16px' }}>
                  {t('Virtual care Only')}
                </Text>
              </VirtualVisitLabel>
            </Flex>
          </Flex>
        </Card.Section>
        <Card.Section>
          <Flex alignItems="center" justify="center">
            <Link
              after={<IconSymbol icon="open_in_new" size="25" />}
              data-auto-testid={`virtual-care-list-get-care-button`}
              data-testid={`virtual-care-list-get-care-button`}
              onClick={(_e) => {
                setIsModalOpen(true);
              }}
            >
              {t('VIRTUAL_CARE_PROVIDERS_BANNER.GET_CARE')}
            </Link>
          </Flex>
        </Card.Section>
      </Card>
      <VirtualCareProviderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedProviderName={provider?.providerName}
        virtualCareProviders={virtualCareProviders}
      />
    </>
  );
};
